import moment from "moment-timezone";
import { useMemo, useState } from "react";
import {
  Edit as EditIcon,
  Plus as PlusIcon,
  Trash as TrashIcon,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  DataTable,
  type DataTableColumn,
} from "@/components/ui/DataTable/index";
import { CommonDestinationTimezone } from "@/lib/utils";
import {
  HedgeBeaconDailyUpdate,
  useListHedgeBeaconDailyUpdates,
} from "../../lib/api/hedgeBeaconDailyUpdates";
import { CreateDailyUpdateModal } from "./daily_updates/CreateDailyUpdateModal";
import { DeleteDailyUpdateModal } from "./daily_updates/DeleteDailyUpdateModal";
import { UpdateDailyUpdateModal } from "./daily_updates/UpdateDailyUpdateModal";

function todayInCommon(): string {
  return moment.tz(CommonDestinationTimezone).format("YYYY-MM-DD");
}

function thirtyDaysAgoInCommon(): string {
  return moment
    .tz(CommonDestinationTimezone)
    .subtract(30, "days")
    .format("YYYY-MM-DD");
}

export function HedgeBeaconDailyUpdates() {
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [dailyUpdateToUpdate, setDailyUpdateToUpdate] = useState<
    HedgeBeaconDailyUpdate | undefined
  >(undefined);
  const [dailyUpdateToDelete, setDailyUpdateToDelete] = useState<
    HedgeBeaconDailyUpdate | undefined
  >(undefined);

  const [filterStartDate, setFilterStartDate] = useState<string>(
    thirtyDaysAgoInCommon()
  );
  const [filterEndDate, setFilterEndDate] = useState<string>(todayInCommon());

  const listQuery = useListHedgeBeaconDailyUpdates({
    startDate: filterStartDate,
    endDate: filterEndDate,
  });

  const dailyUpdates: HedgeBeaconDailyUpdate[] = useMemo(() => {
    return listQuery.data?.success
      ? (listQuery.data.data as HedgeBeaconDailyUpdate[])
      : [];
  }, [listQuery.data]);

  const dailyUpdateColumns: DataTableColumn<HedgeBeaconDailyUpdate>[] = [
    {
      accessorKey: "date",
      header: "Date",
      size: 140,
      cell: ({ row }: { row: { original: HedgeBeaconDailyUpdate } }) =>
        row.original.date,
    },
    {
      accessorKey: "url",
      header: "URL",
      cell: ({ row }: { row: { original: HedgeBeaconDailyUpdate } }) => (
        <a
          href={row.original.url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          {row.original.url}
        </a>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      size: 160,
      enableSorting: false,
      enableGrouping: false,
      enableColumnFilter: false,
      enableHiding: false,
      disableRowClick: true,
      cell: ({ row }: { row: { original: HedgeBeaconDailyUpdate } }) => (
        <div className="flex flex-row gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDailyUpdateToUpdate(row.original)}
          >
            <EditIcon className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDailyUpdateToDelete(row.original)}
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="container flex flex-col mb-2">
        <div className="border-b border-b-2 flex flex-row justify-between items-center">
          <h1 className="scroll-m-20 text-2xl font-bold tracking-tight lg:text-3xl">
            HedgeBeacon Daily Updates
          </h1>
          <span className="flex flex-row items-center">
            {moment.tz(CommonDestinationTimezone).format("dddd M/D/YY")}
          </span>
        </div>
      </div>

      <div className="flex items-start flex-col h-full mt-4">
        <div className="container space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="daily-update-filter-start">Start Date</Label>
              <Input
                id="daily-update-filter-start"
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="daily-update-filter-end">End Date</Label>
              <Input
                id="daily-update-filter-end"
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setCreateModalOpen(true)}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Daily Update
              </Button>
            </div>
          </div>

          <DataTable
            data={dailyUpdates}
            columns={dailyUpdateColumns}
            tableClassName={"min-h-[400px] max-h-[70vh] overflow-y-auto"}
            emptyMessage={
              listQuery.isFetching ? "Loading..." : "No daily updates found."
            }
            stickyHeaders
            enableSorting
            enableFiltering
            enableColumnVisibility
            filterStyle="select"
            filterMode="internal"
            defaultSorting={[
              { id: "date", desc: true },
            ]}
            enableColumnResizing
            enableVirtualization
            virtualizationRowHeight={49}
            virtualizationOverscan={2}
          />
        </div>
      </div>

      <CreateDailyUpdateModal
        open={createModalOpen}
        defaultDate={todayInCommon()}
        onOpenChange={setCreateModalOpen}
      />
      <UpdateDailyUpdateModal
        open={!!dailyUpdateToUpdate}
        dailyUpdate={dailyUpdateToUpdate}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setDailyUpdateToUpdate(undefined);
          }
        }}
      />
      <DeleteDailyUpdateModal
        open={!!dailyUpdateToDelete}
        dailyUpdate={dailyUpdateToDelete}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setDailyUpdateToDelete(undefined);
          }
        }}
      />
    </>
  );
}
